<template>
    <FloatingPopup
        :id="popupId"
        :model-value="show"
        title="Select Home Assistant Entity"
        :width="900"
        :height="620"
        :minwidth="600"
        :minheight="400"
        :opacity="100"
        content-class="p-4 overflow-hidden"
        @update:model-value="$emit('cancel')">

        <div class="flex flex-col h-full min-h-0 gap-3">
            <!-- Search + domain filter -->
            <div class="flex flex-none gap-3">
                <div class="flex-1">
                    <form-input
                        ref="searchInput"
                        type="text"
                        v-model="textFilter"
                        placeholder="Search entity ID or name…"
                        class="w-full" />
                </div>
                <div v-if="!domain" class="w-40 flex-shrink-0">
                    <form-select v-model="domainFilter" class="w-full">
                        <option value="all">All domains</option>
                        <option v-for="d in domains" :key="d.value" :value="d.value">{{ d.label }}</option>
                    </form-select>
                </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="flex flex-1 items-center justify-center text-slate-400">
                Loading entities…
            </div>

            <div v-else-if="!settings?.tauri" class="flex flex-1 items-center justify-center text-slate-500">
                Home Assistant connection is not available.
            </div>

            <!-- Error -->
            <div v-else-if="error" class="flex flex-1 flex-col items-center justify-center gap-2 text-slate-400">
                <p class="text-red-400">{{ error }}</p>
                <form-button @click="fetchEntities">Retry</form-button>
            </div>

            <div v-else-if="!loaded" class="flex flex-1 items-center justify-center text-slate-500">
                Waiting for Home Assistant entities…
            </div>

            <!-- Empty -->
            <div v-else-if="!entities.length" class="flex flex-1 items-center justify-center text-slate-500 italic">
                No entities are available in Home Assistant
            </div>

            <!-- Table -->
            <div v-else class="flex-1 min-h-0 w-full overflow-y-auto overflow-x-hidden rounded border border-slate-600/60">
                <table class="divide-slate-700 w-full table-fixed">
                    <colgroup>
                                <col style="width: 38%;">
                                <col style="width: 38%;">
                                <col style="width: 24%;">
                    </colgroup>
                    <thead class="bg-slate-800/80 sticky top-0">
                        <tr>
                            <th class="px-2 py-1 text-left text-xs font-medium text-slate-400 uppercase tracking-wider truncate">
                                Entity ID</th>
                            <th class="px-2 py-1 text-left text-xs font-medium text-slate-400 uppercase tracking-wider truncate">
                                Name</th>
                            <th class="px-2 py-1 text-left text-xs font-medium text-slate-400 uppercase tracking-wider truncate">
                                State</th>
                        </tr>
                    </thead>
                    <tbody class="bg-slate-900 divide-y divide-slate-700">
                        <tr
                            v-for="entity in filteredEntities"
                            :key="entity.entity_id"
                            class="hover:bg-cyan-900/20 cursor-pointer transition-colors"
                            @click="selectEntity(entity)">
                            <td class="px-2 py-1 font-mono text-xs text-slate-200 truncate" :title="entity.entity_id">
                                {{ entity.entity_id }}</td>
                            <td class="px-2 py-1 text-sm text-slate-300 truncate" :title="entity.attributes.friendly_name || ''">
                                {{ entity.attributes.friendly_name || '' }}</td>
                            <td class="px-2 py-1 text-sm text-slate-400 truncate" :title="entity.state">
                                {{ entity.state }}</td>
                        </tr>
                        <tr v-if="!filteredEntities.length">
                            <td colspan="3" class="px-2 py-4 text-center text-sm text-slate-500 italic">
                                No matching entities
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="footer flex-none w-full flex justify-between items-center">
                <button
                    class="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                    @click="fetchEntities(true)">
                    ↺ Refresh from HA
                </button>
                <form-button class="w-32 py-2" @click="$emit('close')">Close</form-button>
            </div>
        </div>
    </FloatingPopup>
</template>

<script>
import FloatingPopup from '../FloatingPopup.vue';
import { fetchEntities } from '../../services/homeAssistantService.js';

// Selectable domains. Shared with the template so a domain derived from the
// current entity id can be checked against what the dropdown can actually show.
const DOMAINS = [
    { value: 'automation',    label: 'Automation' },
    { value: 'binary_sensor', label: 'Binary sensor' },
    { value: 'climate',       label: 'Climate' },
    { value: 'conversation',  label: 'Conversation' },
    { value: 'cover',         label: 'Cover' },
    { value: 'fan',           label: 'Fan' },
    { value: 'group',         label: 'Group' },
    { value: 'input_boolean', label: 'Input boolean' },
    { value: 'input_number',  label: 'Input number' },
    { value: 'input_select',  label: 'Input select' },
    { value: 'light',         label: 'Light' },
    { value: 'media_player',  label: 'Media player' },
    { value: 'number',        label: 'Number' },
    { value: 'person',        label: 'Person' },
    { value: 'scene',         label: 'Scene' },
    { value: 'script',        label: 'Script' },
    { value: 'select',        label: 'Select' },
    { value: 'sensor',        label: 'Sensor' },
    { value: 'sun',           label: 'Sun' },
    { value: 'switch',        label: 'Switch' },
    { value: 'tag',           label: 'Tag' },
    { value: 'zone',          label: 'Zone' },
];

// Filters survive close/reopen (and remounts of the hosting EntitySelector) so
// browsing several entities in a row doesn't mean re-typing the same search.
const lastFilters = { domainFilter: 'all', textFilter: '' };

// FloatingPopup keys its snap/z-order registry by id, so every instance needs
// its own: multiple EntitySelectors can each host a picker.
let popupSeq = 0;

export default {
    name: 'HomeAssistantEntities',
    components: { FloatingPopup },
    props: {
        show:     { type: Boolean, required: true, default: false },
        settings: { type: Object,  required: true },
        domain:   { type: String,  default: '' },
        // Entity currently held by the field that opened us, if any.
        selected: { type: String,  default: '' },
    },
    emits: ['confirm', 'close', 'cancel'],
    data() {
        return {
            domains:     DOMAINS,
            // Suppresses the filter watchers while prefilling, so an entity we
            // pushed in isn't remembered as the user's own last filter.
            applyingOpeningFilters: false,
            popupId:     `hass-entities-${++popupSeq}`,
            entities:    [],
            domainFilter: this.domain || lastFilters.domainFilter,
            textFilter:  lastFilters.textFilter,
            loading:     false,
            error:       '',
            loaded:      false,
        };
    },
    watch: {
        show: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.applyOpeningFilters();
                    if (!this.entities.length) {
                        this.fetchEntities();
                    }
                    // setTimeout, not nextTick: FloatingPopup focuses its own root
                    // in a nextTick callback queued after ours and would steal it.
                    setTimeout(() => {
                        const input = this.$refs.searchInput?.$el?.querySelector('input');
                        input?.focus();
                        // Pre-selected so typing replaces the prefilled entity id.
                        input?.select();
                    }, 0);
                }
            },
        },
        textFilter(val) {
            if (!this.applyingOpeningFilters) lastFilters.textFilter = val;
        },
        domainFilter(val) {
            // A fixed `domain` prop pins the filter; don't leak it to other pickers.
            if (!this.applyingOpeningFilters && !this.domain) lastFilters.domainFilter = val;
        },
    },
    computed: {
        filteredEntities() {
            return this.entities.filter(entity => {
                const matchesDomain = this.domainFilter === 'all' ||
                    entity.entity_id.startsWith(this.domainFilter + '.');
                const term = this.textFilter.toLowerCase();
                const matchesText = !term ||
                    entity.entity_id.toLowerCase().includes(term) ||
                    (entity.attributes.friendly_name || '').toLowerCase().includes(term);
                return matchesDomain && matchesText;
            });
        },
    },
    methods: {
        // Opening on a field that already holds an entity should show that
        // entity: search on its id, dropdown on its domain. Otherwise fall
        // back to whatever the user last filtered on.
        applyOpeningFilters() {
            this.applyingOpeningFilters = true;
            // Watchers are pre-flush, so release only after they have run.
            this.$nextTick(() => { this.applyingOpeningFilters = false; });
            const current = (this.selected || '').trim();
            if (!current) {
                this.domainFilter = this.domain || lastFilters.domainFilter;
                this.textFilter = lastFilters.textFilter;
                return;
            }
            const currentDomain = current.split('.')[0];
            // An unlisted domain falls back to "all" so the entity stays visible.
            this.domainFilter = this.domain ||
                (DOMAINS.some(d => d.value === currentDomain) ? currentDomain : 'all');
            this.textFilter = current;
        },
        async fetchEntities(refresh = false) {
            this.loading = true;
            this.error   = '';
            try {
                this.entities = await fetchEntities(this.settings, { refresh });
            } catch (e) {
                this.error    = String(e);
                this.entities = [];
            } finally {
                this.loading = false;
                this.loaded = true;
            }
        },
        selectEntity(entity) {
            this.$emit('confirm', entity.entity_id);
        },
    },
};
</script>
